// src/components/shell/Sidebar/Sidebar.tsx
// Top-level sidebar container with responsive variants:
//   • mobile  — hidden drawer behind a hamburger toggle (top-left).
//               Drawer contents: logo + bucket switcher + global items.
//               Modes live in MobileBottomTabBar, mounted by the workspace shell.
//   • rail    — persistent tablet rail (icons only).
//               Contents: logo, bucket switcher, mode list (active bucket only), global items.
//   • desktop — persistent rail with hover-to-expand (150–300ms delay).
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import type { PrimaryMode } from "@/components/shell/modes/modesRegistry";
import { resolveActiveBucket } from "@/components/shell/modes/bucketsRegistry";
import type { SettingsPopoverLeadsControls } from "@/components/shell/popovers/SettingsPopover";
import { BugReportMobileEntry } from "@/components/common/BugReportMobileEntry";
import type { BugReportDevModeSessionInfo } from "@/components/common/BugReportDevModeFooter";
import { BucketSwitcher } from "./BucketSwitcher";
import { SidebarGlobalItems } from "./SidebarGlobalItems";
import {
  SIDEBAR_FOOTER_DOM_ID,
  SidebarKeepOpenControl,
  useSidebarFooterOpen,
  useSidebarKeepOpen,
} from "./SidebarKeepOpenControl";
import { SidebarLocationLinks } from "./SidebarLocationLinks";
import { SidebarLogo } from "./SidebarLogo";
import { SidebarModeList } from "./SidebarModeList";
import { SidebarModeScrollArea } from "./SidebarModeScrollArea";
import { useHoverExpand } from "./useHoverExpand";
import { useSidebarBreakpoint } from "./useSidebarBreakpoint";

/**
 * Optional dev-mode plumbing for the bug-report mobile entry. Mirrors the
 * shape consumed by the desktop floating `BugReportButton` so mobile +
 * desktop stay at parity. Caller passes `null` when there's no
 * dev-mode context available (no-op on mobile).
 */
export type SidebarBugReportProps = {
  devMode?: boolean;
  requestDevMode?: () => void;
  disableDevMode?: () => void;
  getSessionInfo?: () => BugReportDevModeSessionInfo;
};

export type SidebarProps = {
  mode: PrimaryMode;
  setMode: (m: PrimaryMode) => void;
  /** Workspace-specific controls surfaced inside the Settings popover's Leads category. */
  leadsControls?: SettingsPopoverLeadsControls | null;
  /** Used by HelpPopover (greeting copy) and SidebarLocationLinks (popover gate). */
  salesperson?: { name: string } | null;
  /**
   * Used by HelpPopover (location reference) and SidebarLocationLinks
   * (per-location URL substitution + popover subtitle). `id` is the
   * canonical location key; absent for users who haven't selected a
   * location yet.
   */
  location?: { id?: string | null; name: string } | null;
  /** When set, mobile drawer renders a `BugReportMobileEntry` between BucketSwitcher + SidebarGlobalItems. */
  bugReport?: SidebarBugReportProps | null;
};

const RAIL_WIDTH = 64; // px
const EXPANDED_WIDTH = 240; // px

export function Sidebar({
  mode,
  setMode,
  leadsControls = null,
  salesperson = null,
  location = null,
  bugReport = null,
}: SidebarProps) {
  const auth = useAuth();
  const { anchors } = useAnchorEntitlements();
  const variant = useSidebarBreakpoint();
  const [mobileOpenRaw, setMobileOpen] = useState(false);
  const [keepOpen, setKeepOpen] = useSidebarKeepOpen();
  const [footerOpen, setFooterOpen] = useSidebarFooterOpen();
  const hover = useHoverExpand({
    disabled: variant !== "desktop",
    pinned: variant === "desktop" && keepOpen,
  });

  // Derived: drawer is only "open" when the variant is actually mobile.
  // When the viewport upgrades past mobile the drawer is implicitly hidden,
  // and the raw state can sit at whatever value until the user toggles again.
  const mobileOpen = variant === "mobile" && mobileOpenRaw;

  const activeBucketId = resolveActiveBucket(mode, auth, anchors);

  // Mobile drawer expanded state == drawer open.
  // Desktop expanded == hover-driven.
  // Tablet rail never expands (icons only).
  const expanded = variant === "mobile" ? mobileOpen : variant === "desktop" ? hover.expanded : false;

  // Tablet rail + desktop hover-expand share the same DOM; only the width animates.
  // For mobile the drawer is hidden by default so the effective width is 0 — that
  // lets chrome that anchors to `--shell-sidebar-w` (e.g., ActivityFloatingDock on
  // the left edge) sit flush with the viewport without an inset. Dev-sync note 81938292.
  // Hook MUST run unconditionally — keep this above the early mobile return.
  const sidebarWidth = variant === "mobile" ? 0 : expanded ? EXPANDED_WIDTH : RAIL_WIDTH;
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.style.setProperty("--shell-sidebar-w", `${sidebarWidth}px`);
  }, [sidebarWidth]);

  if (variant === "mobile") {
    return (
      <>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
          aria-expanded={mobileOpen}
          className="fixed top-2 left-2 z-40 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95 dark:text-gray-200"
        >
          <span aria-hidden className="text-lg leading-none">≡</span>
        </button>

        {mobileOpen ? (
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px]"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
        ) : null}

        <aside
          className={
            "fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-gray-200 bg-white text-gray-900 shadow-2xl transition-transform duration-200 ease-in-out dark:border-zinc-800 dark:bg-zinc-950 dark:text-gray-100 " +
            (mobileOpen ? "translate-x-0" : "-translate-x-full pointer-events-none")
          }
          role="navigation"
          aria-label="Workspace navigation"
          aria-hidden={!mobileOpen}
          inert={!mobileOpen}
        >
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800">
            <SidebarLogo expanded />
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation"
              className="mr-2 inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              ✕
            </button>
          </div>
          <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
            <BucketSwitcher
              expanded
              mode={mode}
              setMode={setMode}
              onAfterAction={() => setMobileOpen(false)}
            />
          </div>
          {bugReport ? (
            <BugReportMobileEntry
              mode={mode}
              devMode={bugReport.devMode}
              requestDevMode={bugReport.requestDevMode}
              disableDevMode={bugReport.disableDevMode}
              getSessionInfo={bugReport.getSessionInfo}
              onAfterAction={() => setMobileOpen(false)}
            />
          ) : null}
          <SidebarLocationLinks
            expanded
            salesperson={salesperson}
            location={location}
            onAfterAction={() => setMobileOpen(false)}
          />
          <SidebarGlobalItems
            expanded
            onAfterAction={() => setMobileOpen(false)}
            leadsControls={leadsControls}
            mode={mode}
            salesperson={salesperson}
            location={location}
          />
        </aside>
      </>
    );
  }

  // For rendering only — the unconditional `sidebarWidth` above keeps the CSS
  // variable in sync; this local just gives the inline style the same number
  // (mobile takes the early return above, so this branch only sees rail/desktop).
  const inlineWidth = expanded ? EXPANDED_WIDTH : RAIL_WIDTH;

  return (
    <aside
      onMouseEnter={hover.bind.onMouseEnter}
      onMouseLeave={hover.bind.onMouseLeave}
      onFocus={hover.bind.onFocus}
      onBlur={hover.bind.onBlur}
      style={{ width: inlineWidth }}
      className="sticky top-0 z-30 h-screen flex-shrink-0 border-r border-gray-200 bg-white text-gray-900 transition-[width] duration-150 ease-out dark:border-zinc-800 dark:bg-zinc-950 dark:text-gray-100"
      role="navigation"
      aria-label="Workspace navigation"
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center border-b border-gray-200 min-h-[var(--shell-header-h)] dark:border-gray-800">
          <SidebarLogo expanded={expanded} />
        </div>
        <BucketSwitcher expanded={expanded} mode={mode} setMode={setMode} />
        <SidebarModeScrollArea>
          <SidebarModeList
            expanded={expanded}
            mode={mode}
            setMode={setMode}
            activeBucketId={activeBucketId}
          />
        </SidebarModeScrollArea>
        {variant === "desktop" ? (
          <SidebarKeepOpenControl
            expanded={expanded}
            keepOpen={keepOpen}
            onKeepOpenChange={setKeepOpen}
            footerOpen={footerOpen}
            onFooterOpenChange={setFooterOpen}
          />
        ) : null}
        {variant !== "desktop" || footerOpen ? (
          <div id={variant === "desktop" ? SIDEBAR_FOOTER_DOM_ID : undefined}>
            <SidebarLocationLinks
              expanded={expanded}
              salesperson={salesperson}
              location={location}
            />
            <SidebarGlobalItems
              expanded={expanded}
              leadsControls={leadsControls}
              mode={mode}
              salesperson={salesperson}
              location={location}
            />
          </div>
        ) : null}
      </div>
    </aside>
  );
}
